import React, { useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import BarcodeDisplay from "./BarcodeDisplay";

interface BarcodePrintDialogProps {
  open: boolean;
  onClose: () => void;
  barcodeValue: string;
  title?: string;
  subtitle?: string;
  width?: number;
  height?: number;
  fontSize?: number;
  margin?: number;
}

const BarcodePrintDialog: React.FC<BarcodePrintDialogProps> = ({
  open,
  onClose,
  barcodeValue,
  title = "Item Barcode",
  subtitle = "Use this barcode to Print for physical copy and attach that to the item.",
  width = 2,
  height = 60,
  fontSize = 14,
  margin = 10,
}) => {
  const barcodeRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (barcodeRef.current) {
      const svgElement = barcodeRef.current.querySelector('svg');
      if (svgElement) {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          const svgData = new XMLSerializer().serializeToString(svgElement);
          
          printWindow.document.write(`
            <html>
              <head>
                <title>Print Barcode - ${barcodeValue}</title>
                <style>
                  body { 
                    margin: 0; 
                    padding: 20px; 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                    min-height: 100vh; 
                    font-family: Arial, sans-serif;
                    background: white;
                  }
                  svg { 
                    max-width: 100%; 
                    height: auto; 
                  }
                  @media print {
                    body { 
                      padding: 10px; 
                      -webkit-print-color-adjust: exact;
                      print-color-adjust: exact;
                    }
                  }
                </style>
              </head>
              <body>
                ${svgData}
              </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }
      }
    }
  };

  if (!barcodeValue) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: "480px",
        },
      }}
      slotProps={{ 
        backdrop: { 
          sx: { 
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          } 
        } 
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography sx={dialogTitleStyles}>
            {title}
          </Typography>
          <Typography sx={dialogDescriptionStyles}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "grey.400",
            "&:hover": { 
              bgcolor: "grey.100",
              color: "grey.600"
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            mb: 1,
            textAlign: "center",
          }}
        >
          BARCODE
        </Typography>
        
        <div ref={barcodeRef}>
          <BarcodeDisplay 
            value={barcodeValue}
            showPrint={false}
            showLabel={false}
            width={width}
            height={height}
            fontSize={fontSize}
            margin={margin}
            showRequired={false}
            useDialog={false}
          />
        </div>
      </DialogContent>

      <Divider sx={{ ml: 2, mr: 2 }} />
      <DialogActions sx={{ ml: 1, mr: 1, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
            padding: "8px 16px",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            padding: "8px 40px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            },
          }}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodePrintDialog;